import type { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2, Lock, Pencil, Trash2 } from 'lucide-react';
import { useCountries } from '@/features/auth/hooks/useCountries';
import { Button } from '@/common/components/ui/button';
import type { BillingAddress } from '../../services/schemas';

export interface BillingAddressCardProps {
  address: BillingAddress;
  onEdit?: (address: BillingAddress) => void;
  onDelete?: (address: BillingAddress) => void;
  /** When provided, shows a lock icon + this message instead of edit/delete buttons */
  readonlyMessage?: ReactNode;
}

export function BillingAddressCard({
  address,
  onEdit,
  onDelete,
  readonlyMessage,
}: BillingAddressCardProps) {
  const countries = useCountries();
  const countryName = countries.find((c) => c.code === address.country)?.name ?? address.country;

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex size-9 items-center justify-center rounded-xl bg-muted shrink-0">
          <Building2 size={16} className="text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-foreground text-sm truncate">{address.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{address.street}</div>
          <div className="text-xs text-muted-foreground">
            {address.zipCode} {address.city}, {countryName}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-4">
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Trans>Reg. No.</Trans>
          </span>
          <span className="text-foreground font-medium">{address.CRN || '—'}</span>
        </div>
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Trans>VAT ID</Trans>
          </span>
          <span className="text-foreground font-medium">{address.VAT_ID || t`Not registered`}</span>
        </div>
        <div className="col-span-2">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Trans>Email</Trans>
          </span>
          <span className="text-foreground font-medium truncate block">{address.email}</span>
        </div>
      </div>

      <div className="flex items-center pt-3 border-t border-border">
        {readonlyMessage ? (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock size={11} className="shrink-0" />
            {readonlyMessage}
          </span>
        ) : (
          <div className="ml-auto flex items-center gap-1">
            {onEdit && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onEdit(address)}
                aria-label={t`Edit address`}
                className="text-muted-foreground/60 hover:bg-muted hover:text-foreground"
              >
                <Pencil size={14} />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onDelete(address)}
                aria-label={t`Delete address`}
                className="text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
